import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
import {ObjectModelRelationType} from "~/enums/objectModelRelationType";
import {IPeekerOptions} from "~/services/peeker";
import {Hash} from "~/interfaces/hashInterfaces";
import {Constructor} from "../../global/constructor";

export default function ObjectModelRelationContainerBase<TBase extends Constructor>(Base: TBase) {
  return class ObjectModelContainerBase extends Base {
    id: string;
    client_id: string;
    sorted_relation_client_ids: string[];
    object_model_relations: Hash<ObjectModelRelation> = {};
    saveChangesToStore: (peekerOptions: IPeekerOptions) => void;

    get hasRelations(): boolean {
      return Object.keys(this.object_model_relations).length > 0;
    }

    get objectModelRelationsArray(): ObjectModelRelation[] {
      return this.sorted_relation_client_ids.map(clientId => this.object_model_relations[clientId])
        // remove undefined - can happen if have the client_id of relation in the sorted list but do not have the
        // actual object model relation data
        .filter(relation => relation)
    }

    // modelRelations and accountRelations are set up to match the relation scopes on the backend
    // if objectModelRelationsArray is ever causing issues because it includes account relations...
    // then swap it out for modelRelationsArray
    get modelRelationsArray(): ObjectModelRelation[] {
      const modelRelationTypes = [
        ObjectModelRelationType.OneToOne,
        ObjectModelRelationType.OneToMany,
        ObjectModelRelationType.ManyToOne,
      ];
      return this.sorted_relation_client_ids.map(clientId => this.object_model_relations[clientId])
        .filter(relation => relation && modelRelationTypes.includes(relation.relation_type));
    }

    get accountRelationsArray(): ObjectModelRelation[] {
      return this.sorted_relation_client_ids.map(clientId => this.object_model_relations[clientId])
        .filter(relation => relation && relation.relation_type === ObjectModelRelationType.Account);
    }

    get entityAccountRelationsArray(): ObjectModelRelation[] {
      return this.sorted_relation_client_ids.map(clientId => this.object_model_relations[clientId])
        .filter(relation => relation && relation.relation_type === ObjectModelRelationType.EntityAccount);
    }

    findObjectModelRelationImmediate(id: string): ObjectModelRelation {
      return this.objectModelRelationsArray.find(f => f.id === id || f.client_id === id);
    }

    findObjectModelRelation(id: string): ObjectModelRelation {
      let found: ObjectModelRelation = this.findObjectModelRelationImmediate(id);

      if (found) {
        return found;
      }
    }

    findObjectModelRelationContainer(id: string) {
      if (this instanceof ObjectModelContainerBase) {
        if (this.id === id || this.client_id === id) {
          return this;
        }
      }
    }

    objectModelRelationsOfType(...types: ObjectModelRelationType[]): ObjectModelRelation[] {
      return this.objectModelRelationsArray.filter(r => types.includes(r.relation_type));
    }

    modelRelations(): ObjectModelRelation[] {
      return this.objectModelRelationsOfType(
        ObjectModelRelationType.OneToMany,
        ObjectModelRelationType.OneToOne,
        ObjectModelRelationType.ManyToOne,
      )
    }

    accountRelations(): ObjectModelRelation[] {
      return this.objectModelRelationsOfType(ObjectModelRelationType.Account)
    }

    entityAccountRelations(): ObjectModelRelation[] {
      return this.objectModelRelationsOfType(ObjectModelRelationType.EntityAccount)
    }

    allAccountRelations(): ObjectModelRelation[] {
      return this.accountRelations().concat(this.entityAccountRelations());
    }
  };
}